import React, { useState, useEffect } from "react";
import Select from "react-select";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchFeedbackReport } from "../../../store/slices/reportSlice";
import { fetchPatients } from "../../../store/slices/patientsSlice";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MainLayout from "../../../components/layout/MainLayout";

const selectStyles = {
  control: (base) => ({ ...base, minHeight: "31px", height: "31px", fontSize: "0.8125rem", borderRadius: "0.375rem", borderColor: "#d9dee3", "&:hover": { borderColor: "#d9dee3" } }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "31px" }),
  input: (base) => ({ ...base, margin: "0", padding: "0" }),
  indicatorsContainer: (base) => ({ ...base, height: "31px" }),
  menu: (base) => ({ ...base, fontSize: "0.8125rem", zIndex: 9999 }),
  option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#666ee8" : state.isFocused ? "#f5f5f9" : "white", color: state.isSelected ? "white" : "#566a7f", cursor: "pointer" }),
};

// One section: bar chart + data table + comments. Read-only (no inputs).
const SectionBlock = ({ section, respondents, index }) => {
  const categories = section.questions.map((q) => `${q.num}`);
  const series = [
    ...respondents.map((r) => ({ name: r, data: section.questions.map((q) => q.values?.[r] ?? 0) })),
    { name: "Total", data: section.questions.map((q) => q.total) },
  ];

  const options = {
    chart: { type: "bar", height: 340, toolbar: { show: true } },
    plotOptions: { bar: { horizontal: false, columnWidth: "75%", borderRadius: 2 } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ["transparent"] },
    xaxis: { categories, title: { text: "Question" } },
    yaxis: { title: { text: "Rating / Total" } },
    legend: { position: "bottom" },
    fill: { opacity: 1 },
    title: { text: `${section.title}`, align: "center", style: { fontSize: "15px" } },
    tooltip: { y: { formatter: (v) => `${v}` } },
  };

  return (
    <div className="accordion-item">
      <h2 className="accordion-header">
        <button
          className={`accordion-button ${index === 0 ? "" : "collapsed"} fw-semibold`}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target={`#fbSection${index}`}
          aria-expanded={index === 0 ? "true" : "false"}
        >
          {section.title}
        </button>
      </h2>
      <div id={`fbSection${index}`} className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}>
        <div className="accordion-body">
          {respondents.length === 0 ? (
            <div className="text-center text-muted py-4">No feedback forms found for the selected period.</div>
          ) : (
            <>
              {/* Chart */}
              <div className="mb-4">
                <Chart options={options} series={series} type="bar" height={340} />
              </div>

              {/* Data table */}
              <div className="table-responsive mb-4">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "4%" }}>#</th>
                      <th style={{ minWidth: "260px" }}>Question</th>
                      {respondents.map((r) => (<th key={r} className="text-center">{r}</th>))}
                      <th className="text-center fw-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.questions.map((q) => (
                      <tr key={q.num}>
                        <td>{q.num}</td>
                        <td className="small">{q.label}</td>
                        {respondents.map((r) => (
                          <td key={r} className="text-center">{q.values?.[r] ?? "—"}</td>
                        ))}
                        <td className="text-center fw-bold">{q.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Comments */}
              {section.comments && section.comments.length > 0 && (
                <div>
                  <h6 className="fw-bold mb-2">Comments</h6>
                  <ul className="list-group list-group-flush">
                    {section.comments.map((c, i) => (
                      <li className="list-group-item px-0 py-1 small" key={i}>
                        <span className="badge bg-label-primary me-2">{section.key} {c.num}</span>
                        <strong>{c.respondent}:</strong> {c.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const FeedbackReports = () => {
  const dispatch = useDispatch();
  const { feedbackReport, isLoading } = useSelector((state) => state.reports);
  const { patients } = useSelector((state) => state.patients);

  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState({ startDate: "", endDate: "", patientId: "" });
  const [activeFilters, setActiveFilters] = useState({ startDate: "", endDate: "", patientId: "" });

  useEffect(() => { dispatch(fetchPatients()); }, [dispatch]);
  useEffect(() => { dispatch(fetchFeedbackReport(activeFilters)); }, [dispatch, activeFilters]);

  const patientOptions = [
    { value: "", label: "All Patients" },
    ...(patients || []).map((p) => ({ value: p.id, label: `${p.first_name || ""} ${p.last_name || ""} (${p.mr_no || "N/A"})`.trim() })),
  ];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handleSubmit = () => setActiveFilters({ ...tempFilters });
  const handleClear = () => {
    const cleared = { startDate: "", endDate: "", patientId: "" };
    setTempFilters(cleared);
    setActiveFilters(cleared);
  };

  const handleExportPDF = () => {
    if (!feedbackReport) return;
    const doc = new jsPDF("p", "mm", "a4");
    let y = 15;

    // Title
    doc.setFontSize(16);
    doc.text("Feedback Report", 14, y);
    y += 6;

    doc.setFontSize(10);
    doc.text("Family Feedback (Residential)", 14, y);
    y += 6;

    if (period) {
      doc.text(`Period: ${period.from} to ${period.to}`, 14, y);
      y += 6;
    }
    doc.text(`Total Forms: ${feedbackReport.total_forms || 0}`, 14, y);
    y += 10;

    sections.forEach((section, sIdx) => {
      // Check page overflow
      if (y > 240) {
        doc.addPage();
        y = 15;
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(section.title, 14, y);
      y += 5;
      doc.setFont("helvetica", "normal");

      // Table headers
      const headers = [["#", "Question", ...respondents, "Total"]];
      const body = section.questions.map((q) => [
        q.num,
        q.label,
        ...respondents.map((r) => q.values?.[r] ?? "—"),
        q.total
      ]);

      autoTable(doc, {
        startY: y,
        head: headers,
        body: body,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
        columnStyles: {
          1: { cellWidth: 80 } // wrap the question column
        }
      });

      y = doc.lastAutoTable.finalY + 8;

      // Comments for this section
      if (section.comments && section.comments.length > 0) {
        if (y > 250) {
          doc.addPage();
          y = 15;
        }
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Comments:", 14, y);
        y += 5;
        doc.setFont("helvetica", "normal");

        section.comments.forEach((c) => {
          if (y > 270) {
            doc.addPage();
            y = 15;
          }
          doc.setFontSize(8.5);
          const commentText = `${section.key} ${c.num} - ${c.respondent}: ${c.text}`;
          const splitText = doc.splitTextToSize(commentText, 180);
          doc.text(splitText, 14, y);
          y += (splitText.length * 4) + 2;
        });
        y += 4;
      }
    });

    // Final Comments
    if (finalComments && finalComments.length > 0) {
      if (y > 240) {
        doc.addPage();
        y = 15;
      }
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Final Comments", 14, y);
      y += 6;
      doc.setFont("helvetica", "normal");

      finalComments.forEach((c) => {
        if (y > 270) {
          doc.addPage();
          y = 15;
        }
        doc.setFontSize(9);
        const text = `${c.respondent}: ${c.text}`;
        const splitText = doc.splitTextToSize(text, 180);
        doc.text(splitText, 14, y);
        y += (splitText.length * 4) + 2;
      });
    }

    doc.save(`feedback_report_${period ? `${period.from}_to_${period.to}` : "export"}.pdf`);
  };

  const handleExportCSV = () => {
    if (!feedbackReport) return;
    const csvRows = [];

    // General Info
    csvRows.push("Feedback Report");
    if (period) {
      csvRows.push(`Period,${period.from} to ${period.to}`);
    }
    csvRows.push(`Total Forms,${feedbackReport.total_forms || 0}`);
    csvRows.push(""); // empty row

    // Sections
    sections.forEach((section) => {
      csvRows.push(`"${section.title.replace(/"/g, '""')}"`);

      // Header row for this section
      const sectionHeaders = ["#", "Question"];
      respondents.forEach((r) => sectionHeaders.push(r));
      sectionHeaders.push("Total");
      csvRows.push(sectionHeaders.map(h => `"${h.replace(/"/g, '""')}"`).join(","));

      // Questions rows
      section.questions.forEach((q) => {
        const row = [q.num, `"${q.label.replace(/"/g, '""')}"`];
        respondents.forEach((r) => {
          row.push(q.values?.[r] ?? "—");
        });
        row.push(q.total);
        csvRows.push(row.join(","));
      });

      // Comments for this section
      if (section.comments && section.comments.length > 0) {
        csvRows.push("");
        csvRows.push("Comments");
        csvRows.push("Question #,Respondent,Comment");
        section.comments.forEach((c) => {
          csvRows.push(`"${section.key} ${c.num}","${c.respondent.replace(/"/g, '""')}","${c.text.replace(/"/g, '""')}"`);
        });
      }
      csvRows.push(""); // spacer between sections
    });

    // Final Comments
    if (finalComments && finalComments.length > 0) {
      csvRows.push("Final Comments");
      csvRows.push("Respondent,Comment");
      finalComments.forEach((c) => {
        csvRows.push(`"${c.respondent.replace(/"/g, '""')}","${c.text.replace(/"/g, '""')}"`);
      });
    }

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `feedback_report_${period ? `${period.from}_to_${period.to}` : "export"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const respondents = feedbackReport?.respondents || [];
  const sections = feedbackReport?.sections || [];
  const finalComments = feedbackReport?.finalComments || [];
  const period = feedbackReport?.period;

  return (
    <MainLayout>
      <Breadcrumbs />
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0 text-dark">Feedback Report</h4>
            <small className="text-muted">
              Family Feedback (Residential)
              {period ? ` · ${period.from} to ${period.to}` : ""}
              {feedbackReport ? ` · ${feedbackReport.total_forms} form(s)` : ""}
            </small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <style jsx="true" global="true">{`
              @media print {
                .accordion-collapse {
                  display: block !important;
                  height: auto !important;
                  visibility: visible !important;
                }
                .accordion-button {
                  color: #000 !important;
                  background-color: transparent !important;
                }
                .accordion-button::after {
                  display: none !important;
                }
                .layout-navbar,
                .layout-menu,
                .btn,
                .card-header button,
                .breadcrumbs,
                .card-header div,
                .footer {
                  display: none !important;
                }
                .card {
                  border: none !important;
                  box-shadow: none !important;
                }
                body {
                  background-color: #fff !important;
                }
              }
            `}</style>
            <div className="dropdown">
              <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="ri-download-2-line me-1"></i> Export
              </button>
              <ul className="dropdown-menu shadow-sm">
                <li>
                  <button className="dropdown-item" onClick={handleExportPDF}>
                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => window.print()}>
                    <i className="ri-printer-line me-1 text-secondary"></i> Print
                  </button>
                </li>
              </ul>
            </div>
            <button className={`btn btn-sm ${showFilters ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setShowFilters(!showFilters)}>
              <i className="ri-filter-line me-1"></i> {showFilters ? "Hide Filters" : "Filters"}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`collapse ${showFilters ? "show" : ""}`}>
          <div className="px-4 py-3">
            <div className="card shadow-sm border border-light">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label small fw-bold">Date From</label>
                    <input type="date" name="startDate" className="form-control form-control-sm" value={tempFilters.startDate} onChange={handleFilterChange} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-bold">Date To</label>
                    <input type="date" name="endDate" className="form-control form-control-sm" value={tempFilters.endDate} onChange={handleFilterChange} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-bold">Patient</label>
                    <Select options={patientOptions} styles={selectStyles} value={patientOptions.find((opt) => opt.value === tempFilters.patientId)} onChange={(opt) => setTempFilters((prev) => ({ ...prev, patientId: opt ? opt.value : "" }))} placeholder="Select Patient" isSearchable />
                  </div>
                  <div className="col-md-3 d-flex align-items-end gap-2">
                    <button className="btn btn-sm btn-label-secondary w-50" onClick={handleClear}>Clear</button>
                    <button className="btn btn-sm btn-primary w-50" onClick={handleSubmit}>Submit</button>
                  </div>
                </div>
                <div className="text-muted small mt-2">
                  <i className="ri-information-line me-1"></i>
                  By default the current month's feedback forms are shown.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
          ) : (
            <>
              <div className="accordion" id="feedbackReportAccordion">
                {sections.map((section, idx) => (
                  <SectionBlock key={section.key} section={section} respondents={respondents} index={idx} />
                ))}
              </div>

              {/* Final Comments */}
              {finalComments.length > 0 && (
                <div className="card border mt-4">
                  <div className="card-header fw-bold">Final Comments</div>
                  <div className="card-body">
                    <ul className="list-group list-group-flush">
                      {finalComments.map((c, i) => (
                        <li className="list-group-item px-0 small" key={i}>
                          <strong>{c.respondent}:</strong> {c.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default FeedbackReports;
